import { useColorMode } from '@chakra-ui/react';

import { useFeatureFlag } from '@bonusly/who-am-i';

export type ColorModeValue = 'light' | 'dark' | 'system';

/**
 * Hook to access and control the user's color mode preference
 *
 * Returns the current color mode and functions to change it.
 * Users can only change their color mode preference if they have the 'dark-mode-beta' feature flag.
 * Without the feature flag, only light mode is available.
 *
 * Note: Color mode enforcement (resetting users to light mode when they don't have access)
 * is handled by SeasonalThemeProvider to avoid conflicts with seasonal theme dark mode forcing.
 *
 * Supports three modes:
 * - 'light': Always use light theme
 * - 'dark': Always use dark theme
 * - 'system': Follow the user's system preference
 */
export const useUserColorMode = () => {
	const hasFeatureFlag = useFeatureFlag('dark-mode-beta');
	const { colorMode, toggleColorMode, setColorMode } = useColorMode();

	return {
		/** Current resolved color mode ('light' | 'dark') - what's actually being displayed */
		colorMode,
		/** True if currently displaying dark mode */
		isDarkMode: colorMode === 'dark',
		/** True if user has the feature flag to change their color mode preference */
		canChangeColorMode: hasFeatureFlag,
		/** Function to toggle between light and dark mode */
		toggleColorMode,
		/** Function to set specific color mode ('light', 'dark', or 'system') */
		setColorMode: setColorMode as (mode: ColorModeValue) => void,
	};
};
